// @flow
import * as React from 'react';
import { types as t } from './validation/runtime-checks/index.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { illustrationColor } from './designSystem.js';

/**
 * ExternalLink is a simple component used to create links/anchors to non-mathspace URLs.
 *
 * We want to disambiguate between internal and external links as internal links will
 * often have to interop with our in-app routing layer. It may also make sense to
 * give users a visual indicator that they will leave Mathspace by clicking this link.
 * This is especially true when you are consuming Mathspace in the React Native app.
 */
type Props = {|
  url: string,
  children: string,
|};

export default function ExternalLink(props: Props) {
  const { failedValidation } = usePropsValidation(props, ExternalLink);
  if (failedValidation) return null;

  // We'll use this _blank behaviour since this is taking you to an
  // external domain.
  // TODO styles haven't been specified anywhere AFAICT.
  return (
    <a
      style={{ color: illustrationColor.lochmara }}
      target="_blank"
      href={props.url}
    >
      {props.children}
    </a>
  );
}
ExternalLink.spec = {
  url: nonMathspaceUrlChecker,
  children: t.string,
};

// Enforces that a valid URL is passed that does NOT link to a mathspace domain
export function nonMathspaceUrlChecker(props: Object, propName: string) {
  const value = props[propName];

  const failure = t.string(props, propName);
  if (failure !== null) return failure;
  // Refinement for Flow as it doesn't know t.string enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  let url;
  try {
    url = new URL(value);
  } catch (error) {
    return {
      typeDescription: nonMathspaceUrlChecker.description,
      reason: 'must contain a valid URL. For example: https://google.com',
    };
  }

  // We do the check this way to ensure that no subdomains (now or in the future) get through.
  if (url.hostname.includes('mathspace')) {
    return {
      typeDescription: nonMathspaceUrlChecker.description,
      reason:
        'must not be a Mathspace URL. This component is for EXTERNAL links.',
    };
  }

  return null;
}
nonMathspaceUrlChecker.description = 'nonMathspaceUrl';
